/* eslint-disable react-hooks/exhaustive-deps */
'use client';

import InnerLayout from '~/components/InnerLayout';
import Fieldset from '~/components/commonUi/Fieldset';
import Button from '~/components/commonUi/Button/Button';
import Input from '~/components/commonUi/Input/Input';
import { observer } from 'mobx-react';
import { getStore } from '~/mobx/AppStore';
import { InputChangeState, InputErrorState } from '~/definitions/standAdmin';
import { useEffect } from 'react';

interface StandAdminProps {
    inputChangeState: InputChangeState;
    onReset: (e: React.MouseEvent<HTMLAnchorElement>) => void;
    onInputChange: (updates: Partial<InputChangeState>) => void;
    onSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
    inputErrors?: InputErrorState;
}

const StandAdmin = (props: StandAdminProps) => {
    const { inputChangeState, onReset, onInputChange, onSubmit, inputErrors } = props;

    const renderMainContent = () => {
        const rowClassName = 'grid grid-cols-1 lg:grid-cols-12 w-full lg:w-6/12 mb-4 items-center';
        const firstColClassName = 'col-span-12 lg:col-span-4 mb-[6px] lg:mb-[0] text-left font-normal';
        const secondColClassName = 'col-span-12 lg:col-span-8';
        const inputExtendedClassName = 'w-full';

        const resetText = 'Tøm skjema';

        return (
            <Fieldset>
                <>
                    <p className="mb-4">Identifiser medeier med CoopID og fødselsdato for å registrere stemme.</p>
                    <p className="mb-4">
                        <a
                            id="reset"
                            title={resetText}
                            href={'/'}
                            onClick={onReset}
                            className="underline text-[#83ab34] visited:text-[#83ab34]"
                        >
                            {resetText}
                        </a>
                    </p>
                    <form
                        action=""
                        method="post"
                        className={'frm-stand-admin'}
                        data-test-id={'frm-stand-admin'}
                        onSubmit={onSubmit}
                    >
                        <div className={rowClassName}>
                            <label htmlFor="coopId" className={firstColClassName}>
                                CoopID <span className="text-red-500">*</span>
                            </label>
                            <div className={secondColClassName}>
                                <Input
                                    id={'coopId'}
                                    name={'coopId'}
                                    placeholder={'Tast inn CoopID'}
                                    onChange={(e) => onInputChange({ coopId: e.target.value })}
                                    value={inputChangeState.coopId}
                                    testId={'coopId-input'}
                                    maxLength={10}
                                    extendedClassName="w-full text-[100%] placeholder:text-[100%]"
                                    error={!!inputErrors?.coopId}
                                />
                            </div>
                        </div>
                        <div className={rowClassName}>
                            <label htmlFor="birthdate" className={firstColClassName}>
                                Fødselsdato <span className="text-red-500">*</span>
                            </label>
                            <div className={secondColClassName}>
                                <Input
                                    id={'birthdate'}
                                    name={'birthdate'}
                                    placeholder={'Dato på format DDMMÅÅÅÅ'}
                                    onChange={(e) => onInputChange({ birthdate: e.target.value })}
                                    value={inputChangeState.birthdate}
                                    testId={'birthdate-input'}
                                    maxLength={8}
                                    extendedClassName={inputExtendedClassName}
                                    error={!!inputErrors?.birthdate}
                                />
                            </div>
                        </div>
                        <div className={rowClassName}>
                            <div className={firstColClassName}></div>
                            <div className={secondColClassName}>
                                <Button
                                    type={'submit'}
                                    btnText={'Gå til stemmeseddel'}
                                    testId={'submit-stand-admin'}
                                    onClick={() => onSubmit}
                                    extendedClassName={inputExtendedClassName}
                                />
                            </div>
                        </div>
                    </form>
                </>
            </Fieldset>
        );
    };

    return <InnerLayout className="stand-admin-page" content={renderMainContent()} />;
};

const StandAdminWithStore = () => {
    const { companyStore, electionMenuStore, standAdminStore } = getStore();

    const onReset = (e: React.MouseEvent<HTMLAnchorElement>) => {
        e.preventDefault();
        standAdminStore.resetToDefault();
    };

    const handleOnSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        standAdminStore.onSubmit();
    };

    useEffect(() => {
        electionMenuStore.getElections();
    }, [companyStore.companyList, companyStore.selectedSlagId]);

    return (
        <StandAdmin
            inputChangeState={standAdminStore.inputChangeState}
            onReset={onReset}
            onInputChange={standAdminStore.onInputChange}
            onSubmit={handleOnSubmit}
            inputErrors={standAdminStore.inputErrors}
        />
    );
};

export default observer(StandAdminWithStore);
